// src/pages/LearnMore.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faClipboardCheck,
  faUserShield,
  faBolt,
  faGlobe,
  faShareAlt,
  faChalkboardTeacher,
  faUsers,
  faCirclePlay,
} from "@fortawesome/free-solid-svg-icons";

import "../styles/LearnMore.css";
import { ROUTES } from "../config";

const LearnMore = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrollY(y);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="learnmore-container" style={{ "--scrollY": `${scrollY}px` }}>
      <Helmet>
        <title>How GhrianGhraffy Works - Principal Workflow</title>
        <meta
          name="description"
          content="See how GhrianGhraffy helps Irish Primary School principals control and safely share photos with parents, your website and social media — without added workload."
        />
      </Helmet>

      <main>
        {/* Hero */}
        <header className="lm-hero">
          <div className="lm-hero-inner">
            <div className="lm-hero-copy">
              <div className="lm-pill-row">
                <span className="lm-pill">Built for Principals</span>
                <span className="lm-pill lm-pill-outline">Privacy-first • GDPR-minded</span>
              </div>

              <h1>See how GhrianGhraffy works — in a Principal-led workflow.</h1>
              <p className="lm-tagline">
                Teachers capture moments. You approve what’s shared. Parents stay connected — safely.
                Optional sharing to your school website and Instagram, controlled by you.
              </p>

              <div className="lm-cta-row">
                <Link to={ROUTES.PRINCIPAL_REGISTRATION} className="lm-cta primary">
                  Start Free Trial
                </Link>
                <a href="#demo" className="lm-cta secondary">
                  <FontAwesomeIcon icon={faCirclePlay} />
                  <span>Watch the 90s Demo</span>
                </a>
              </div>

              <div className="lm-hero-bullets">
                <div className="lm-bullet">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                  <span>Nothing is shared without approval</span>
                </div>
                <div className="lm-bullet">
                  <FontAwesomeIcon icon={faBolt} />
                  <span>Less admin work, fewer messages, no chasing folders</span>
                </div>
              </div>
            </div>

            {/* Video placeholder (swap to real embed later) */}
            <div className="lm-hero-video" id="demo">
              <div className="lm-video-card">
                <div className="lm-video-header">
                  <span className="lm-video-dot" />
                  <span className="lm-video-dot" />
                  <span className="lm-video-dot" />
                </div>

                <div className="lm-video-body">
                  <div className="lm-video-placeholder">
                    <FontAwesomeIcon icon={faCirclePlay} className="lm-video-icon" />
                    <div className="lm-video-text">
                      <strong>Demo video placeholder</strong>
                      <p>
                        Drop in a YouTube/Vimeo/Loom link later — this box is ready for it.
                      </p>
                    </div>
                  </div>

                  <div className="lm-video-notes">
                    <div className="lm-note">
                      <FontAwesomeIcon icon={faChalkboardTeacher} />
                      <span>Staff capture in seconds</span>
                    </div>
                    <div className="lm-note">
                      <FontAwesomeIcon icon={faUserShield} />
                      <span>Principal approval controls sharing</span>
                    </div>
                    <div className="lm-note">
                      <FontAwesomeIcon icon={faUsers} />
                      <span>Parents view securely</span>
                    </div>
                    <div className="lm-note">
                      <FontAwesomeIcon icon={faGlobe} />
                      <span>Optional website gallery</span>
                    </div>
                    <div className="lm-note">
                      <FontAwesomeIcon icon={faShareAlt} />
                      <span>Optional Instagram posting</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="lm-video-help">
                Want this to be a real video next? Send me the link you plan to use (YouTube/Vimeo/Loom)
                and I’ll embed it cleanly + responsively.
              </p>
            </div>
          </div>
        </header>

        {/* Steps */}
        <section className="lm-section">
          <div className="lm-inner">
            <div className="lm-section-header">
              <p className="lm-eyebrow">How it works</p>
              <h2>From classroom to parents in 3 steps</h2>
              <p className="lm-lead">
                GhrianGhraffy is designed so you remain in control, without turning photo sharing into
                an admin burden.
              </p>
            </div>

            <div className="lm-steps">
              <article className="lm-step">
                <div className="lm-step-num">1</div>
                <h3>Capture</h3>
                <p>
                  Staff take photos (and video) in the moment using the GhrianGhraffy app — no
                  messy camera roll, no sending files around.
                </p>
              </article>

              <article className="lm-step">
                <div className="lm-step-num">2</div>
                <h3>Approve</h3>
                <p>
                  You approve what’s suitable to share. Clear queue, one-click decisions, and you
                  can nominate staff to help if you want.
                </p>
              </article>

              <article className="lm-step">
                <div className="lm-step-num">3</div>
                <h3>Share</h3>
                <p>
                  Approved photos are visible to parents securely. Optional publishing to your
                  school website (via plugin) and Instagram — only if you enable it.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Principal value */}
        <section className="lm-section lm-muted">
          <div className="lm-inner">
            <div className="lm-section-header">
              <p className="lm-eyebrow">Designed for principals</p>
              <h2>Control, safety, and less workload</h2>
            </div>

            <div className="lm-cards">
              <article className="lm-card">
                <div className="lm-card-icon">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                </div>
                <h3>Approval-led sharing</h3>
                <p>
                  No “oops” posts. Nothing goes out to parents or your website unless it’s approved.
                </p>
              </article>

              <article className="lm-card">
                <div className="lm-card-icon">
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
                <h3>Privacy-first approach</h3>
                <p>
                  Built to keep school photos out of random chat groups and uncontrolled sharing loops.
                </p>
              </article>

              <article className="lm-card">
                <div className="lm-card-icon">
                  <FontAwesomeIcon icon={faBolt} />
                </div>
                <h3>Less admin noise</h3>
                <p>
                  Replace WhatsApp chains, USB sticks, and folder chaos with one consistent workflow.
                </p>
              </article>
            </div>

            <div className="lm-cta-band">
              <div>
                <h3>See it working for your school</h3>
                <p>Start your free trial and explore the principal dashboard today.</p>
              </div>
              <Link to={ROUTES.PRINCIPAL_REGISTRATION} className="lm-cta primary">
                Get Started
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LearnMore;
